import { useRef, useState } from 'react'

interface Props {
  images: string[]   // ordered list of image URLs
  height?: number
  showThumbs?: boolean
}

export default function ImageCarousel({ images, height = 200, showThumbs = false }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)

  if (images.length === 0) return null

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const index = Math.round(el.scrollLeft / el.offsetWidth)
    setActiveIndex(index)
  }

  const scrollToIndex = (index: number) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ left: index * el.offsetWidth, behavior: 'smooth' })
    setActiveIndex(index)
  }

  return (
    <div className="w-full">
      <div className="relative w-full" style={{ height }}>
        {/* Scrollable strip */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto snap-x snap-mandatory w-full h-full"
          style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
          onScroll={handleScroll}
        >
          {images.map((url, i) => (
            <div key={i} className="snap-center flex-shrink-0 w-full h-full">
              <img src={url} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>

        {/* Dots — only when not showing thumbnails */}
        {!showThumbs && images.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
            {images.map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all"
                style={{
                  width: i === activeIndex ? 16 : 6,
                  height: 6,
                  background: i === activeIndex ? 'white' : 'rgba(255,255,255,0.5)',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail strip — only when showThumbs */}
      {showThumbs && images.length > 1 && (
        <div
          className="flex gap-1.5 overflow-x-auto mt-1.5"
          style={{ scrollbarWidth: 'none', paddingBottom: 2 }}
        >
          {images.map((url, i) => (
            <div
              key={i}
              onClick={() => scrollToIndex(i)}
              style={{
                width: 52,
                height: 52,
                flexShrink: 0,
                borderRadius: 8,
                overflow: 'hidden',
                cursor: 'pointer',
                border: i === activeIndex
                  ? '2px solid var(--tg-theme-button-color)'
                  : '2px solid transparent',
                opacity: i === activeIndex ? 1 : 0.5,
                transition: 'opacity 0.15s',
              }}
            >
              <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
